import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, ShieldCheck } from "lucide-react";
import { Header } from "@/components/Header";
import { CartSheet } from "@/components/CartSheet";
import { useCart } from "@/context/CartContext";
import { track } from "@/lib/analytics";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORY_LABELS = {
  "storage-boxes": "Storage Boxes",
  "display-cases": "Display Cases",
  "shoe-care": "Shoe Care",
};

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const { addItem } = useCart();

  useEffect(() => {
    axios.get(`${API}/products/${id}`)
      .then((r) => { setProduct(r.data); setActiveImg(0); track("product_view", { product_id: id, path: `/product/${id}` }); })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-6 py-24 text-center">
          <p className="font-display font-semibold text-xl" data-testid="product-not-found">Product not found.</p>
          <Link to="/shop" className="inline-block mt-6 bg-foreground text-white px-6 py-3 text-sm uppercase tracking-wider hover:bg-primary transition-colors">Back to Store</Link>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="py-24 text-center text-muted-foreground text-sm uppercase tracking-widest">Loading...</div>
      </div>
    );
  }

  const out = product.stock <= 0;
  const gallery = (product.images && product.images.length > 0) ? product.images : (product.image_url ? [product.image_url] : []);
  const primary = gallery[activeImg] || gallery[0];

  return (
    <div className="min-h-screen bg-background" data-testid="product-detail-page">
      <Header />
      <CartSheet />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Link to="/shop" data-testid="product-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
          <ArrowLeft size={16} /> Back to the collection
        </Link>
        <div className="grid md:grid-cols-2 gap-12">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
            <div data-testid="product-gallery-primary" className="bg-secondary border border-border aspect-square overflow-hidden">
              {primary ? (
                <img key={primary} src={primary} alt={product.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs uppercase tracking-widest">No Image</div>
              )}
            </div>
            {gallery.length > 1 && (
              <div data-testid="product-gallery-thumbs" className="mt-3 grid grid-cols-6 gap-2">
                {gallery.map((src, i) => (
                  <button
                    key={`${src}-${i}`}
                    data-testid={`gallery-thumb-${i}`}
                    onClick={() => setActiveImg(i)}
                    className={`aspect-square border overflow-hidden transition-colors ${i === activeImg ? "border-foreground" : "border-border hover:border-foreground/60"}`}
                  >
                    <img src={src} alt={`${product.name} view ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="text-xs uppercase tracking-[0.3em] text-primary font-medium">{CATEGORY_LABELS[product.category] || product.category}</span>
            <h1 data-testid="product-detail-name" className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-4">{product.name}</h1>
            <p data-testid="product-detail-price" className="font-display font-semibold text-2xl mt-4">${product.price.toFixed(2)}</p>
            {product.description && (
              <p data-testid="product-detail-description" className="text-muted-foreground mt-6 leading-relaxed whitespace-pre-line">{product.description}</p>
            )}
            <div className="mt-8 flex items-center gap-3 text-sm text-muted-foreground">
              <ShieldCheck size={16} className="text-primary" />
              {out ? "Currently sold out" : `${product.stock} in stock — ships fast, packed to protect`}
            </div>
            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center border border-border">
                <button data-testid="detail-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-11 flex items-center justify-center hover:bg-secondary transition-colors"><Minus size={14} /></button>
                <span data-testid="detail-qty" className="w-10 text-center text-sm font-medium">{qty}</span>
                <button data-testid="detail-qty-plus" onClick={() => setQty(Math.min(qty + 1, product.stock))} className="w-11 h-11 flex items-center justify-center hover:bg-secondary transition-colors"><Plus size={14} /></button>
              </div>
              <button
                data-testid="detail-add-to-cart"
                disabled={out}
                onClick={() => { addItem(product, qty); toast.success(`${product.name} added to cart`); }}
                className="flex-1 bg-primary text-white py-3 px-8 text-sm font-medium uppercase tracking-wider hover:bg-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {out ? "Sold Out" : "Add to Cart"}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
